import { useEffect, useMemo, useRef, useState } from "react";
import { BusyLabel, HitLoadingBlock } from "@/components/loading";
import { Link, useSearchParams } from "react-router-dom";
import { api, publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { isSealed } from "@/lib/productType";
import { variantsForCard, conditionsForCard } from "@/lib/gameConfig";
import { conditionOptions } from "@/lib/conditions";
import { invalidateCatalogCache } from "@/lib/catalogCache";
import { useAuth } from "@/lib/auth";
import { useDialog } from "@/lib/dialog";
import { useLightbox } from "@/lib/lightbox";
import { sellerNetFor } from "@/lib/pricing";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import MyReputationCard from "@/components/MyReputationCard";
import { BankSelect } from "@/components/BankSelect";
import { EditProfileCard, digitsOnly } from "@/components/EditProfileCard";
import MySlugCard from "@/components/MySlugCard";
import ShareCatalogModal from "@/components/ShareCatalogModal";
import {
  Package, DollarSign, TrendingUp, AlertTriangle, PlusCircle, Trash2, Landmark,
  CheckCircle2, Truck, Pencil, ShieldCheck, Share2, Wallet, Store, ArrowRight, Star, Camera,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GameTabs } from "@/components/GameTabs";
import GamePreferencesCard from "@/components/GamePreferencesCard";
import { RankProfileHeader, RankDetails, ChangeAvatarButton } from "@/components/avatar/RankPanel";

const STATUS_CLASS = {
  PENDIENTE_PAGO: "status-pill status-pendiente", PAGADO: "status-pill status-pagado",
  ENVIADO: "status-pill status-enviado", ENTREGADO: "status-pill status-entregado",
  PENDIENTE_LIBERACION: "status-pill status-entregado",
  LIBERADO: "status-pill status-liberado", EN_DISPUTA: "status-pill status-disputa",
  REEMBOLSADO: "status-pill status-disputa", RESUELTA_PARCIAL: "status-pill status-disputa",
  CANCELADO: "status-pill status-pendiente",
};

export default function SellerDashboard() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const { open: openLightbox } = useLightbox();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    if (["catalog", "orders", "money", "profile"].includes(t)) return t;
    if (t === "listings") return "catalog";
    if (t === "payout" || t === "shipping") return "profile";
    return "catalog";
  })();
  const [tab, setTab] = useState(initialTab);
  // Deep-link target dentro de la pestaña Perfil: cuando llegamos con
  // `?tab=payout` (o `shipping`) queremos hacer scroll suave hasta esa
  // sección + resaltarla brevemente, para que el usuario NO se pierda
  // entre las tarjetas del perfil. Guardamos el subtarget crudo del URL.
  const initialSubTarget = (() => {
    const t = searchParams.get("tab");
    return t === "payout" || t === "shipping" ? t : null;
  })();
  const payoutSectionRef = useRef(null);
  const shippingSectionRef = useRef(null);
  const [highlightSection, setHighlightSection] = useState(null); // "payout" | "shipping" | null
  const [orders, setOrders] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [payout, setPayout] = useState(undefined);
  const [shipping, setShipping] = useState(undefined);
  const [reputation, setReputation] = useState({ avg: null, count: 0, sales: 0 });
  const [loading, setLoading] = useState(true);
  const [editingPub, setEditingPub] = useState(null);
  // Fase 2 · segmentación por juego para Mis Ventas y Mis Publicaciones.
  const [salesGame, setSalesGame] = useState("all");
  const [pubsGame, setPubsGame] = useState("all");

  const reload = async () => {
    setLoading(true);
    const [o, p, pi, sc, prof, cfg] = await Promise.all([
      api.get("/orders/mine", { params: { role: "seller", ...(salesGame !== "all" ? { game: salesGame } : {}) } }),
      api.get("/publications", { params: { seller_id: user.id, only_active: false, limit: 100, ...(pubsGame !== "all" ? { game: pubsGame } : {}) } }),
      api.get("/seller/payout-info").catch(() => ({ data: null })),
      api.get("/seller/shipping-config").catch(() => ({ data: null })),
      api.get(`/sellers/${user.id}/profile`).catch(() => ({ data: null })),
      api.get("/config/public").catch(() => ({ data: null })),
    ]);
    setOrders(o.data); setPubs(p.data); setPayout(pi.data || null); setShipping(sc.data || null);
    setTiers(cfg.data?.commission_tiers || []);
    if (prof.data) setReputation({ avg: prof.data.rating?.avg ?? null, count: prof.data.rating?.count ?? 0, sales: prof.data.sales_count ?? 0 });
    setLoading(false);
  };

  useEffect(() => { if (user) reload(); /* eslint-disable-next-line */ }, [user, salesGame, pubsGame]);

  const totalHeld = orders.filter(o => ["PAGADO", "ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION"].includes(o.status)).reduce((a, o) => a + o.net_seller, 0);
  const totalEarned = orders.filter(o => o.status === "LIBERADO").reduce((a, o) => a + o.net_seller, 0);

  const remove = async (pubId) => {
    const ok = await confirm({ title: "Eliminar publicación", description: "¿Estás seguro? Se ocultará del catálogo.", destructive: true, confirmText: "Eliminar" });
    if (!ok) return;
    await api.delete(`/publications/${pubId}`);
    // Cache del catálogo desactualizado tras eliminar — refresca on-mount.
    try { invalidateCatalogCache(); } catch { /* noop */ }
    toast.success("Eliminada");
    reload();
  };

  const changeTab = (v) => { setTab(v); setSearchParams(v === "catalog" ? {} : { tab: v }); };

  // Deep-link scroll + highlight: cuando el URL trae `?tab=payout` o
  // `?tab=shipping`, después de renderizar la pestaña Perfil hacemos scroll
  // suave a la sección correspondiente y le aplicamos un ring rojo pulsante
  // durante 2.5s. Sin esto, el usuario que viene desde el bloqueo de
  // "Publicar" veía la pestaña Perfil arriba del todo y no encontraba
  // dónde llenar los datos. Solo se dispara una vez por navegación.
  useEffect(() => {
    if (!initialSubTarget || tab !== "profile" || loading) return;
    const target = initialSubTarget === "shipping" ? shippingSectionRef.current : payoutSectionRef.current;
    if (!target) return;
    // Doble RAF para asegurar que el DOM ya montó los hijos de <TabsContent>
    // (Shadcn `Tabs` monta el contenido de la pestaña activa on demand).
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        // Offset para que quede debajo del header sticky (aprox 80px).
        const rect = target.getBoundingClientRect();
        const y = rect.top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: "smooth" });
        setHighlightSection(initialSubTarget);
      });
      return () => cancelAnimationFrame(raf2);
    });
    // Apagar el highlight tras 2.5s
    const off = setTimeout(() => setHighlightSection(null), 2500);
    return () => { cancelAnimationFrame(raf1); clearTimeout(off); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, loading, initialSubTarget]);

  const canPublish = !!payout;

  const orderByPubId = useMemo(() => {
    const m = {};
    for (const o of orders) if (o.publication_id) m[o.publication_id] = o;
    return m;
  }, [orders]);

  const [shareOpen, setShareOpen] = useState(false);
  const shareCatalog = () => setShareOpen(true);

  return (
    <div className="max-w-7xl mx-auto px-4 pt-8 pb-24 md:pb-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl">PANEL DEL VENDEDOR</h1>
          <p className="text-sm text-neutral-500 mt-1">Catálogo (producto) · Ventas (transacción) · Dinero (contabilidad) · Perfil (identidad).</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            type="button"
            data-testid="seller-share-catalog"
            onClick={shareCatalog}
            variant="outline"
            className="border-[#EF4444]/50 text-[#EF4444] hover:bg-[#EF4444]/10 flex-1 sm:flex-none"
          >
            <Share2 size={16} className="mr-2" />Compartir mi catálogo
          </Button>
          {canPublish ? (
            <Link to="/publicar" className="flex-1 sm:flex-none"><Button data-testid="seller-publish-btn" className="bg-[#EF4444] hover:bg-[#DC2626] w-full"><PlusCircle size={16} className="mr-2" />Publicar carta</Button></Link>
          ) : (
            <Button data-testid="seller-publish-btn-blocked" onClick={() => changeTab("profile")} className="bg-neutral-700 hover:bg-neutral-600 flex-1 sm:flex-none"><Landmark size={16} className="mr-2" />Configura Datos de Cobro</Button>
          )}
        </div>
      </div>

      {payout === null && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-md p-4 mb-6 flex items-start gap-3" data-testid="payout-warning-banner">
          <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <div className="font-semibold text-amber-200">Aún no puedes publicar cartas</div>
            <div className="text-sm text-neutral-300 mt-1">Configura tus datos de cobro en la pestaña <b>Perfil</b> para poder publicar.</div>
          </div>
          <Button size="sm" onClick={() => changeTab("profile")} className="bg-[#EF4444] hover:bg-[#DC2626]">Ir a Perfil</Button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat icon={Package} label="Cartas en catálogo" value={pubs.filter(p => p.status === "active").length} />
        <Stat icon={DollarSign} label="Retenido (por liberar)" value={`$${totalHeld.toFixed(2)}`} />
        <Stat icon={TrendingUp} label="Cobrado (liberado)" value={`$${totalEarned.toFixed(2)}`} />
        <Stat icon={AlertTriangle} label="Strikes" value={user?.strikes || 0} highlight={user?.strikes > 0} />
      </div>

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList className="bg-[#111] border border-[#262626] flex-wrap h-auto">
          <TabsTrigger data-testid="seller-tab-catalog" value="catalog" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <Store size={14} className="mr-1" />Mi Catálogo ({pubs.length})
          </TabsTrigger>
          <TabsTrigger data-testid="seller-tab-orders" value="orders" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <Truck size={14} className="mr-1" />Mis Ventas ({orders.length})
          </TabsTrigger>
          <TabsTrigger data-testid="seller-tab-money" value="money" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <Wallet size={14} className="mr-1" />Mi Dinero
          </TabsTrigger>
          <TabsTrigger data-testid="seller-tab-profile" value="profile" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
            <ShieldCheck size={14} className="mr-1" />Perfil / Verificación
          </TabsTrigger>
        </TabsList>

        {/* ==== MI CATÁLOGO — sólo producto ==== */}
        <TabsContent value="catalog" className="mt-4">
          <GameTabs value={pubsGame} onChange={setPubsGame} testidPrefix="seller-pubs" className="mb-4" />
          {/* IMPORTANTE — visible solo al vendedor en su propio catálogo. Explica
              la política de sobreventa: si vendió una carta por fuera, DEBE dar
              de baja la publicación. Si un comprador logra pagarla, el vendedor
              recibe un strike (además del reembolso automático). */}
          <div
            data-testid="seller-catalog-stock-disclaimer"
            className="mb-3 p-3 rounded-md bg-red-500/10 border border-red-500/40 flex items-start gap-3"
          >
            <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
            <div className="text-[12px] text-red-100 leading-relaxed">
              <div className="font-semibold text-red-200 mb-1">IMPORTANTE — Mantén tu inventario actualizado</div>
              Si vendes una carta <b>por fuera de HitTCG</b>, da de baja esa publicación de <b>INMEDIATO</b>. Si un comprador intenta comprar una carta que ya no tienes, se le reembolsará y <b className="text-red-200">recibirás un strike por incumplimiento</b> (al 3er strike se bloquea la cuenta automáticamente).
            </div>
          </div>
          <div className="mb-3 text-xs text-neutral-500 p-2 rounded bg-[#0A0A0A] border border-[#262626]">
            <b className="text-white">Mi Catálogo:</b> aquí gestionas el <b>producto</b> (fotos, precio, condición). El envío de una carta vendida vive en <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("orders")}>Mis Ventas</button> y la contabilidad de esa venta en <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("money")}>Mi Dinero</button>.
          </div>
          {pubs.length === 0 ? (
            <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500">
              Sin cartas en tu catálogo. {canPublish ? <Link to="/publish" className="text-[#EF4444]">Publica la primera</Link> : <button onClick={() => changeTab("profile")} className="text-[#EF4444] hover:underline">Configura Perfil / Datos de Cobro para poder publicar</button>}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {pubs.map(p => {
                const rel = orderByPubId[p.id];
                const photoCount = p.photos?.length || 0;
                return (
                  <div key={p.id} data-testid={`seller-pub-${p.id}`} className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
                    <div className="relative aspect-[3/4] bg-[#0A0A0A]">
                      {p.card?.image_url ? <img src={cardImageUrl(p.card.image_url)} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} /> :
                        p.photos?.[0] ? <img src={publicImageUrl(p.photos[0])} alt="" className="w-full h-full object-cover" /> :
                        <div className="text-xs text-neutral-600 text-center p-4">Sin imagen</div>}
                      {photoCount > 0 && (
                        <button
                          type="button"
                          onClick={() => openLightbox(p.photos.map(ph => ({ path: ph, scope: "public" })), 0)}
                          data-testid={`seller-pub-photos-${p.id}`}
                          title={`Ver ${photoCount} foto${photoCount === 1 ? "" : "s"} reales`}
                          className="absolute top-1 left-1 bg-black/80 hover:bg-[#EF4444] border border-[#262626] rounded-full px-2 py-0.5 text-[10px] text-white flex items-center gap-1 transition-colors"
                        >
                          <Camera size={10} /> {photoCount}
                        </button>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="text-sm line-clamp-1">{cardName(p.card, p.language)}</div>
                      <div className="flex justify-between mt-2">
                        <span className="font-display text-lg text-[#EF4444]">${p.price.toFixed(2)}</span>
                        <span className="text-xs text-neutral-500">{p.language?.toUpperCase()} · {p.condition}</span>
                      </div>
                      {/* Model B — show what the seller will actually receive if this
                          publication sells at the listed price. Buyer sees only price;
                          seller sees the net after HitTCG + Payphone commissions. */}
                      {p.status !== "sold" && (() => {
                        const b = sellerNetFor(p.price, tiers);
                        return (
                          <div
                            className="mt-1 text-[11px] text-emerald-400 cursor-help"
                            data-testid={`seller-pub-net-${p.id}`}
                            title="Neto por carta (preview). No incluye envío ni la porción de Payphone del envío. En cada orden ves el desglose completo con envío incluido."
                          >
                            Recibirás <b>${b.net.toFixed(2)}</b>
                            <span className="text-neutral-500"> · HitTCG −${b.hittcg.toFixed(2)} · Payphone −${b.payphone.toFixed(2)}</span>
                          </div>
                        );
                      })()}
                      <div className="mt-2 flex justify-between items-center">
                        <span className={`text-[10px] uppercase ${p.status === "active" ? "text-emerald-400" : p.status === "sold" ? "text-neutral-500" : "text-amber-400"}`}>
                          {p.status === "pending_review" ? "en revisión" : p.status}
                        </span>
                        {p.status !== "sold" && (
                          <div className="flex gap-1">
                            <Button data-testid={`seller-edit-${p.id}`} size="sm" variant="ghost" onClick={() => setEditingPub(p)} className="text-neutral-500 hover:text-emerald-400 h-6 w-6 p-0"><Pencil size={14} /></Button>
                            <Button data-testid={`seller-delete-${p.id}`} size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-neutral-500 hover:text-[#EF4444] h-6 w-6 p-0"><Trash2 size={14} /></Button>
                          </div>
                        )}
                      </div>
                      {p.status === "pending_review" && p.review_reason && (
                        <div className="mt-1 text-[10px] text-amber-400 line-clamp-2" title={p.review_reason}>{p.review_reason}</div>
                      )}
                      {p.status === "sold" && rel && (
                        <Link
                          to={`/order/${rel.id}`}
                          data-testid={`catalog-goto-order-${p.id}`}
                          className="mt-2 flex items-center justify-between text-[11px] text-[#EF4444] hover:underline"
                        >
                          Ver pedido #{rel.id.slice(0, 8)} <ArrowRight size={12} />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ==== MIS VENTAS — sólo envío / estado ==== */}
        <TabsContent value="orders" className="mt-4">
          <GameTabs value={salesGame} onChange={setSalesGame} testidPrefix="seller-sales" className="mb-4" />
          <div className="mb-3 text-xs text-neutral-500 p-2 rounded bg-[#0A0A0A] border border-[#262626]">
            <b className="text-white">Mis Ventas:</b> aquí gestionas <b>envío y estado del pedido</b>. Para editar la carta como producto vuelve a <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("catalog")}>Mi Catálogo</button>. Para desglose y liberaciones ve a <button className="text-[#EF4444] hover:underline" onClick={() => changeTab("money")}>Mi Dinero</button>.
          </div>
          {loading ? <HitLoadingBlock section="inventory" /> : orders.length === 0 ? (
            <div className="border border-dashed border-[#262626] rounded-md p-12 text-center text-neutral-500">Aún no tienes ventas.</div>
          ) : (
            <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-neutral-500 text-xs uppercase tracking-wider border-b border-[#262626]">
                  <tr>
                    <th className="text-left p-3">Pedido</th>
                    <th className="text-left p-3">Carta</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-left p-3">Envío</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const its = o.items && o.items.length ? o.items : [{ card: o.card, quantity: o.quantity, publication_language: o.publication_language }];
                    const shown = its.slice(0, 5);
                    const extra = Math.max(0, its.length - shown.length);
                    return (
                    <tr key={o.id} data-testid={`seller-order-${o.id}`} className="border-b border-[#171717]">
                      <td className="p-3 font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {shown.map((it, i) => (
                            it.card?.image_url ? (
                              <img key={i} src={cardImageUrl(it.card.image_url)} alt={cardName(it.card, it.publication_language) || ""} title={cardName(it.card, it.publication_language) || ""} className="w-8 h-11 object-contain rounded-sm border border-[#262626]" onError={(e) => e.currentTarget.style.display = "none"} />
                            ) : (
                              <div key={i} className="w-8 h-11 bg-[#0A0A0A] border border-[#262626] rounded-sm" />
                            )
                          ))}
                          {extra > 0 && <span className="text-xs text-neutral-400 font-medium ml-1" data-testid={`seller-order-extra-${o.id}`}>+{extra}</span>}
                          <div className="ml-2 min-w-0">
                            <div className="text-white text-sm line-clamp-1">
                              {its.length > 1 ? `${o.item_count || its.reduce((s,x)=>s+(x.quantity||1),0)} cartas` : (cardName(its[0]?.card, its[0]?.publication_language) || "—")}
                            </div>
                            {its.length > 1 && (
                              <div className="text-neutral-500 text-xs line-clamp-1">{cardName(its[0]?.card, its[0]?.publication_language)} +{its.length - 1}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-3"><span className={STATUS_CLASS[o.status]}>{o.status}</span></td>
                      <td className="p-3 text-neutral-400 text-xs">
                        {o.delivery_method === "meetup" ? <span className="text-emerald-400">Encuentro</span> :
                         o.guide_number ? <span className="text-white">Guía {o.guide_number}</span> :
                         <span className="text-amber-400">Falta despachar</span>}
                      </td>
                      <td className="p-3 text-right"><Link to={`/order/${o.id}`} data-testid={`order-manage-${o.id}`} className="text-[#EF4444] text-sm hover:underline">Gestionar envío →</Link></td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ==== MI DINERO — contabilidad ==== */}
        <TabsContent value="money" className="mt-4">
          <MoneyTab orders={orders} onGoOrders={() => changeTab("orders")} />
        </TabsContent>

        {/* ==== PERFIL / VERIFICACIÓN ==== */}
        <TabsContent value="profile" className="mt-4 space-y-6">
          <ProfileCard user={user} reputation={reputation} />
          <RankDetails />
          <MySlugCard user={user} />
          <EditProfileCard />
          <GamePreferencesCard />
          <MyReputationCard />
          <div
            ref={payoutSectionRef}
            id="payout-section"
            data-testid="payout-section"
            className={`scroll-mt-24 rounded-md transition-all duration-500 ${highlightSection === "payout" ? "ring-2 ring-[#EF4444] ring-offset-2 ring-offset-[#0A0A0A] shadow-[0_0_40px_rgba(239,68,68,0.35)]" : ""}`}
          >
            <PayoutForm currentPayout={payout} user={user} onSaved={reload} />
          </div>
          <div
            ref={shippingSectionRef}
            id="shipping-section"
            data-testid="shipping-section"
            className={`scroll-mt-24 rounded-md transition-all duration-500 ${highlightSection === "shipping" ? "ring-2 ring-[#EF4444] ring-offset-2 ring-offset-[#0A0A0A] shadow-[0_0_40px_rgba(239,68,68,0.35)]" : ""}`}
          >
            <ShippingForm currentConfig={shipping} sellerProvince={user?.province} onSaved={reload} />
          </div>
          <ChangePasswordCard />
        </TabsContent>
      </Tabs>

      <EditPubDialog pub={editingPub} onClose={() => setEditingPub(null)} onSaved={reload} />
      <ShareCatalogModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        sellerName={user?.name || "mi catálogo"}
        slug={user?.slug}
        fallbackUrl={user?.id ? `/vendedor/${user.id}` : null}
      />
    </div>
  );
}

// ================ MI DINERO ================
function MoneyTab({ orders, onGoOrders }) {
  const held = orders.filter(o => ["PAGADO", "ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION"].includes(o.status));
  const released = orders.filter(o => o.status === "LIBERADO");
  const refunded = orders.filter(o => ["REEMBOLSADO", "RESUELTA_PARCIAL", "CANCELADO"].includes(o.status));

  const totalHeld = held.reduce((a, o) => a + (o.net_seller || 0), 0);
  const totalReleased = released.reduce((a, o) => a + (o.net_seller || 0), 0);
  const totalGross = orders.reduce((a, o) => a + (o.price || 0), 0);
  const totalCommission = orders.reduce((a, o) => a + (o.commission || 0), 0);

  return (
    <div className="space-y-6" data-testid="money-tab">
      <div className="text-xs text-neutral-500 p-2 rounded bg-[#0A0A0A] border border-[#262626]">
        <b className="text-white">Mi Dinero:</b> <b>desglose contable</b> y <b>liberaciones</b>. Para gestionar el envío de una venta ve a <button className="text-[#EF4444] hover:underline" onClick={onGoOrders}>Mis Ventas</button>.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MoneyStat label="Bruto total" value={`$${totalGross.toFixed(2)}`} />
        <MoneyStat label="Comisión total" value={`-$${totalCommission.toFixed(2)}`} muted />
        <MoneyStat label="Retenido" value={`$${totalHeld.toFixed(2)}`} highlight="amber" />
        <MoneyStat label="Liberado (cobrado)" value={`$${totalReleased.toFixed(2)}`} highlight="emerald" />
      </div>

      <MoneyBlock title="Liberaciones (pagos recibidos)" icon={Landmark} iconClass="text-emerald-400" count={released.length}>
        {released.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500 text-center">Todavía no tienes pagos liberados. Se listarán aquí cuando el admin transfiera tus ventas cerradas.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">Pedido</th>
                <th className="text-left p-3">Carta</th>
                <th className="text-right p-3">Bruto</th>
                <th className="text-right p-3">HitTCG</th>
                <th className="text-right p-3">Payphone</th>
                <th className="text-right p-3">Neto liberado</th>
                <th className="text-left p-3">Comprobante</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {released.map(o => (
                <tr key={o.id} data-testid={`money-row-${o.id}`} className="border-t border-[#171717]">
                  <td className="p-3 text-xs text-neutral-400">{o.released_at ? new Date(o.released_at).toLocaleDateString("es-EC") : "—"}</td>
                  <td className="p-3 font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</td>
                  <td className="p-3 text-neutral-200 truncate max-w-[220px]" data-testid={`money-card-${o.id}`}>
                    {(o.items && o.items.length > 1)
                      ? `${o.item_count || o.items.length} cartas · ${cardName(o.items[0]?.card, o.items[0]?.publication_language) || ""} +${o.items.length - 1}`
                      : (cardName(o.items?.[0]?.card, o.items?.[0]?.publication_language) || cardName(o.card, o.publication_language) || "—")}
                  </td>
                  <td className="p-3 text-right text-neutral-400">${o.price.toFixed(2)}</td>
                  <td className="p-3 text-right text-neutral-500">-${(o.commission || 0).toFixed(2)}</td>
                  <td className="p-3 text-right text-neutral-500">-${(o.payphone_fee ?? (o.price * 0.0575)).toFixed(2)}</td>
                  <td className="p-3 text-right font-display text-emerald-400">${o.net_seller.toFixed(2)}</td>
                  <td className="p-3 text-xs text-neutral-400">
                    {o.release_receipt?.number ? <span className="font-mono">{o.release_receipt.number}</span> : "—"}
                    {o.release_receipt?.date && <div className="text-[10px] text-neutral-500">{o.release_receipt.date}</div>}
                  </td>
                  <td className="p-3 text-right"><Link to={`/order/${o.id}`} className="text-[#EF4444] text-xs hover:underline">Ver detalle →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </MoneyBlock>

      <MoneyBlock title="Retenido (aún no cobrado)" icon={DollarSign} iconClass="text-amber-400" count={held.length}>
        {held.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500 text-center">No tienes ventas con dinero retenido.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Pedido</th>
                <th className="text-left p-3">Carta</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Neto retenido</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {held.map(o => (
                <tr key={o.id} data-testid={`money-held-${o.id}`} className="border-t border-[#171717]">
                  <td className="p-3 font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</td>
                  <td className="p-3 text-neutral-200 truncate max-w-[220px]" data-testid={`held-card-${o.id}`}>
                    {(o.items && o.items.length > 1)
                      ? `${o.item_count || o.items.length} cartas · ${cardName(o.items[0]?.card, o.items[0]?.publication_language) || ""} +${o.items.length - 1}`
                      : (cardName(o.items?.[0]?.card, o.items?.[0]?.publication_language) || cardName(o.card, o.publication_language) || "—")}
                  </td>
                  <td className="p-3"><span className={STATUS_CLASS[o.status]}>{o.status}</span></td>
                  <td className="p-3 text-right font-display text-amber-400">${o.net_seller.toFixed(2)}</td>
                  <td className="p-3 text-right"><Link to={`/order/${o.id}`} className="text-[#EF4444] text-xs hover:underline">Gestionar →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </MoneyBlock>

      {refunded.length > 0 && (
        <MoneyBlock title="Reembolsados / cancelados" icon={AlertTriangle} iconClass="text-red-400" count={refunded.length}>
          <table className="w-full text-sm">
            <tbody>
              {refunded.map(o => (
                <tr key={o.id} className="border-t border-[#171717]">
                  <td className="p-3 font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</td>
                  <td className="p-3">
                    {(o.items && o.items.length > 1)
                      ? `${o.item_count || o.items.length} cartas · ${cardName(o.items[0]?.card, o.items[0]?.publication_language) || ""} +${o.items.length - 1}`
                      : (cardName(o.items?.[0]?.card, o.items?.[0]?.publication_language) || cardName(o.card, o.publication_language) || "—")}
                  </td>
                  <td className="p-3"><span className={STATUS_CLASS[o.status]}>{o.status}</span></td>
                  <td className="p-3 text-right text-neutral-500 line-through">${o.net_seller.toFixed(2)}</td>
                  <td className="p-3 text-right"><Link to={`/order/${o.id}`} className="text-neutral-400 text-xs hover:underline">Ver →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </MoneyBlock>
      )}
    </div>
  );
}

function MoneyBlock({ title, icon: Icon, iconClass, count, children }) {
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
        <Icon size={16} className={iconClass} /> <span className="font-display text-lg">{title}</span>
        <span className="ml-auto text-xs text-neutral-500">{count} pedido{count === 1 ? "" : "s"}</span>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

function MoneyStat({ label, value, muted, highlight }) {
  const color = highlight === "emerald" ? "text-emerald-400" : highlight === "amber" ? "text-amber-400" : muted ? "text-neutral-500" : "text-white";
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-4">
      <div className="text-neutral-500 text-xs uppercase tracking-wider">{label}</div>
      <div className={`font-display text-2xl mt-2 ${color}`}>{value}</div>
    </div>
  );
}

// ================ PERFIL ================
function ProfileCard({ user, reputation }) {
  const isStore = user?.account_type === "store";
  const statusBadge = {
    aprobado: { label: "Verificado", cls: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
    pendiente_revision: { label: "En revisión", cls: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
    pendiente_documentos: { label: "Faltan documentos", cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/30" },
    rechazado: { label: "Rechazado", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  }[user?.verification_status] || { label: user?.verification_status || "—", cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/30" };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="profile-card">
      <RankProfileHeader user={user} avatarTestid="seller-dash-avatar" identity={<>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl">{user?.name}</h2>
            <Badge className={statusBadge.cls}><ShieldCheck size={12} className="mr-1" />{statusBadge.label}</Badge>
            {isStore && <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30"><Store size={12} className="mr-1" />Tienda</Badge>}
          </div>
          <div className="text-sm text-neutral-400 mt-1 break-all">
            {user?.email} · {user?.phone || "sin teléfono"} · {user?.city || "—"}, {user?.province || "—"}
          </div>
          {isStore && (
            <div className="text-xs text-purple-300 mt-1">
              Tienda: <b>{user?.store_name}</b> · RUC {user?.ruc}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ChangeAvatarButton testid="seller-change-avatar" />
            {user?.verification_status !== "aprobado" && (
              <Link to="/verify" className="mt-2"><Button size="sm" data-testid="profile-goto-verify" className="bg-[#EF4444] hover:bg-[#DC2626]">Subir documentos</Button></Link>
            )}
          </div>
      </>} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[#262626]">
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Reputación</div>
          <div className="font-display text-xl mt-1 flex items-center gap-1">
            {reputation.avg !== null ? <><Star size={16} className="fill-[#EF4444] text-[#EF4444]" />{reputation.avg} <span className="text-xs text-neutral-500">({reputation.count})</span></> : <span className="text-neutral-500 text-sm">Sin votos</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Ventas cerradas</div>
          <div className="font-display text-xl mt-1">{reputation.sales}</div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Strikes</div>
          <div className={`font-display text-xl mt-1 ${user?.strikes > 0 ? "text-red-400" : "text-neutral-400"}`}>{user?.strikes || 0} / 3</div>
        </div>
        <div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Cédula</div>
          <div className="font-mono text-sm mt-2 text-neutral-300">{user?.cedula || "—"}</div>
        </div>
      </div>
    </div>
  );
}

// ================ EDIT PUB DIALOG ================
function EditPubDialog({ pub, onClose, onSaved }) {
  const [form, setForm] = useState({ price: "", quantity: 1, condition: "NM", ship_cost: 0, variant: "normal", language: "en" });
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([]);
  const { open: openLightbox } = useLightbox();
  // Los productos sellados no llevan variante ni condición de carta — se
  // publican de fábrica. Ocultamos esos campos para que el vendedor no vea un
  // Select que no aplica (y para no reenviar variant="sealed" a un validador
  // que espera "normal|reverse_holo|price_pack").
  const sealed = pub ? isSealed(pub) : false;
  useEffect(() => {
    api.get("/config/public").then(r => setTiers(r.data?.commission_tiers || [])).catch(() => {});
  }, []);
  const breakdown = sellerNetFor(form.price, tiers);

  useEffect(() => {
    if (pub) {
      setForm({
        price: pub.price ?? "",
        quantity: pub.quantity ?? 1,
        condition: pub.condition ?? "NM",
        ship_cost: pub.ship_cost ?? 0,
        variant: pub.variant ?? "normal",
        language: pub.language ?? "es",
      });
    }
  }, [pub]);

  if (!pub) return null;

  const save = async () => {
    setSaving(true);
    try {
      // Solo enviamos los campos que realmente aplican al tipo de ítem.
      // Sellados: no llevan variante/condición de carta → los omitimos.
      const payload = {
        price: parseFloat(form.price),
        quantity: parseInt(form.quantity),
        ship_cost: parseFloat(form.ship_cost),
        language: form.language,
      };
      if (!sealed) {
        payload.condition = form.condition;
        payload.variant = form.variant;
      }
      await api.put(`/publications/${pub.id}`, payload);
      // Feb 2026 — editar precio/cantidad/condición/variante puede sacar la
      // pub del catálogo (a pending_review) o cambiar su ficha. Invalidamos
      // el cache para que el vendedor vea el efecto sin cerrar la pestaña.
      try { invalidateCatalogCache(); } catch { /* noop */ }
      toast.success("Cambios guardados. La publicación vuelve a revisión del admin.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!pub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-pub-dialog" className="bg-[#111] border-[#262626] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Editar publicación</DialogTitle>
        </DialogHeader>
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-200 mb-3">
          Cualquier cambio hace que la publicación vuelva a <b>revisión del administrador</b>. Se ocultará del catálogo hasta ser reaprobada.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-neutral-400">Precio USD (lo que paga el comprador)</Label>
            <Input data-testid="edit-price" type="number" step="0.01" min="0.01" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Cantidad</Label>
            <Input
              data-testid="edit-quantity"
              type="number"
              inputMode="numeric"
              step="1"
              min="1"
              value={form.quantity ?? ""}
              onFocus={(e) => e.target.select()}
              onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))}
              onBlur={(e) => { if (e.target.value === "" || parseInt(e.target.value, 10) < 1) setForm(f => ({ ...f, quantity: 1 })); }}
              className="bg-[#171717] border-[#262626]"
            />
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Condición</Label>
            {sealed ? (
              <div className="bg-[#171717] border border-[#262626] rounded px-3 py-2 text-sm text-neutral-500 italic" data-testid="edit-condition-sealed">
                No aplica (producto sellado)
              </div>
            ) : (
              <Select value={form.condition} onValueChange={(v) => setForm(f => ({ ...f, condition: v }))}>
                <SelectTrigger data-testid="edit-condition" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626] text-white">
                  {conditionOptions(form.condition === "MIXED").map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Variante</Label>
            {sealed ? (
              <div className="bg-[#171717] border border-[#262626] rounded px-3 py-2 text-sm text-neutral-500 italic" data-testid="edit-variant-sealed">
                No aplica (producto sellado)
              </div>
            ) : (
              <Select value={form.variant} onValueChange={(v) => setForm(f => ({ ...f, variant: v }))}>
                <SelectTrigger data-testid="edit-variant" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626] text-white">
                  {/* Variantes derivadas del juego de la carta (feb 2026 fix
                      "editar Riftbound muestra variantes Pokémon"). El helper
                      hace grandfather de valores legacy: si `form.variant`
                      no está en la whitelist actual, lo agrega igual para no
                      romper edits históricos. */}
                  {variantsForCard(pub?.card, form.variant).map(v => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Idioma</Label>
            <Select value={form.language} onValueChange={(v) => setForm(f => ({ ...f, language: v }))}>
              <SelectTrigger data-testid="edit-language" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white">
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="en">Inglés</SelectItem>
                <SelectItem value="ja">Japonés</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-neutral-400">Envío base ($)</Label>
            <Input data-testid="edit-ship-cost" type="number" step="0.01" min="0" value={form.ship_cost} onChange={(e) => setForm(f => ({ ...f, ship_cost: e.target.value }))} className="bg-[#171717] border-[#262626]" />
          </div>
        </div>
        {/* Live net breakdown — updates as the seller changes the price. Same
            treatment as PublishCard so el vendedor ve un rango honesto.
            Feb 2026: la comisión Payphone es CONDICIONAL al método de
            pago que elija el comprador. Mostramos las dos cifras. */}
        {breakdown.price > 0 && (
          <div className="mt-3 p-3 bg-[#0A0A0A] border border-[#EF4444]/30 rounded text-sm" data-testid="edit-net-breakdown">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Estimación de lo que recibirás</div>
            <div className="flex justify-between"><span className="text-neutral-300">Precio de venta</span><span className="text-white font-semibold">${breakdown.price.toFixed(2)}</span></div>
            <div className="flex justify-between mt-1"><span className="text-neutral-400">Comisión HitTCG{breakdown.activeTier ? (breakdown.minFeeApplied ? ` (mínimo $${breakdown.minFee.toFixed(2)})` : ` (${breakdown.activeTier.pct}%)`) : ""}</span><span className="text-neutral-500">−${breakdown.hittcg.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-400">Comisión Payphone (5.75%) — solo si tarjeta</span><span className="text-neutral-500">−${breakdown.payphone.toFixed(2)}</span></div>
            <div className="pt-2 mt-2 border-t border-[#262626] grid grid-cols-2 gap-2">
              <div className="p-2 rounded bg-purple-500/5 border border-purple-500/20">
                <div className="text-[9px] uppercase tracking-wider text-purple-300 mb-0.5">Si pagan con tarjeta</div>
                <div className="text-emerald-400 font-display text-lg" data-testid="edit-seller-receives-card">${breakdown.net.toFixed(2)}</div>
              </div>
              <div className="p-2 rounded bg-blue-500/5 border border-blue-500/20">
                <div className="text-[9px] uppercase tracking-wider text-blue-300 mb-0.5">Si pagan por transferencia</div>
                <div className="text-emerald-400 font-display text-lg" data-testid="edit-seller-receives-transfer">${(breakdown.net + breakdown.payphone).toFixed(2)}</div>
              </div>
            </div>
            <div className="text-[10px] text-neutral-500 mt-2 leading-relaxed" data-testid="edit-estimation-note">
              Estimación por carta. El monto real depende del método de pago del comprador: con <b className="text-purple-300">tarjeta</b> se descuenta el 5.75% de Payphone, con <b className="text-blue-300">transferencia</b> no aplica. En cada orden ves el desglose exacto con envío incluido.
            </div>
          </div>
        )}
        {pub.photos?.length > 0 && (
          <div className="mt-3">
            <div className="text-xs text-neutral-500 mb-2">Fotos actuales ({pub.photos.length}) · toca cualquiera para verla en grande</div>
            <div className="flex gap-2 overflow-x-auto">
              {pub.photos.map((ph, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => openLightbox(pub.photos.map(x => ({ path: x, scope: "public" })), i)}
                  data-testid={`edit-pub-photo-${i}`}
                  className="shrink-0 focus:outline-none focus:ring-2 focus:ring-[#EF4444] rounded"
                >
                  <img src={publicImageUrl(ph, 200)} alt="" className="w-16 h-20 object-cover rounded border border-[#262626] hover:border-[#EF4444] transition-colors" />
                </button>
              ))}
            </div>
            <div className="text-[10px] text-neutral-500 mt-1">Para reemplazar fotos, elimina la publicación y créala nueva.</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button data-testid="edit-pub-save" onClick={save} disabled={saving} className="bg-[#EF4444] hover:bg-[#DC2626]">
            <BusyLabel busy={saving} text="Guardando…">Guardar y enviar a revisión</BusyLabel>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================ ENVÍO ================
const EC_PROVINCES_UI = [
  "Azuay", "Bolívar", "Cañar", "Carchi", "Chimborazo", "Cotopaxi",
  "El Oro", "Esmeraldas", "Galápagos", "Guayas", "Imbabura", "Loja",
  "Los Ríos", "Manabí", "Morona Santiago", "Napo", "Orellana", "Pastaza",
  "Pichincha", "Santa Elena", "Santo Domingo de los Tsáchilas", "Sucumbíos",
  "Tungurahua", "Zamora Chinchipe",
];

export function ShippingForm({ currentConfig, sellerProvince, onSaved }) {
  const [mode, setMode] = useState(currentConfig?.mode || "simple");
  const [same, setSame] = useState(currentConfig?.same_province_rate ?? 3.5);
  const [other, setOther] = useState(currentConfig?.other_province_rate ?? (currentConfig ? "" : 5.5));
  const [rates, setRates] = useState(currentConfig?.per_province_rates || {});
  const [meetup, setMeetup] = useState(currentConfig?.meetup_enabled || false);
  const [notes, setNotes] = useState(currentConfig?.meetup_notes || "");
  const [localOnly, setLocalOnly] = useState(!!currentConfig?.local_only);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentConfig) {
      setMode(currentConfig.mode || "simple");
      setSame(currentConfig.same_province_rate ?? 3.5);
      setOther(currentConfig.other_province_rate ?? "");
      setRates(currentConfig.per_province_rates || {});
      setMeetup(!!currentConfig.meetup_enabled);
      setNotes(currentConfig.meetup_notes || "");
      setLocalOnly(!!currentConfig.local_only);
    }
  }, [currentConfig]);

  const submit = async (e) => {
    e.preventDefault();
    if (meetup && !notes.trim()) {
      toast.error("Si activas encuentro presencial, indica los lugares/puntos de encuentro");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        mode,
        same_province_rate: mode === "simple" ? parseFloat(same) : null,
        other_province_rate: mode === "simple" && other !== "" ? parseFloat(other) : null,
        per_province_rates: mode === "detailed" ? Object.fromEntries(Object.entries(rates).map(([k, v]) => [k, parseFloat(v || 0)])) : null,
        meetup_enabled: !!meetup,
        meetup_notes: notes,
        local_only: !!localOnly,
      };
      await api.put("/seller/shipping-config", payload);
      toast.success("Configuración de envío guardada");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="shipping-form-container">
      <div className="flex items-center gap-2 mb-2">
        <Truck size={20} className="text-[#EF4444]" />
        <h2 className="font-display text-2xl">Configuración de Envío</h2>
      </div>
      <p className="text-sm text-neutral-400 mb-4">
        Tú fijas cuánto cobras por envío. Se usa para calcular el costo al comprador según su provincia. <b className="text-white">Tu provincia:</b> {sellerProvince || "—"}.
      </p>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Modo</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="shipping-mode" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="simple">Simple: mismo/otras provincias</SelectItem>
              <SelectItem value="detailed">Detallado: tarifa por provincia</SelectItem>
              <SelectItem value="meetup_only">Solo encuentro presencial</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {mode === "simple" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Misma provincia ($)</Label>
              <Input data-testid="ship-same" type="number" step="0.01" min="0" value={same} onChange={(e) => setSame(e.target.value)} required className="bg-[#171717] border-[#262626]" />
            </div>
            <div>
              <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Otras provincias ($)</Label>
              <Input data-testid="ship-other" type="number" step="0.01" min="0" value={other} onChange={(e) => setOther(e.target.value)} required={!localOnly} placeholder={localOnly ? "No aplica (solo tu provincia)" : ""} className="bg-[#171717] border-[#262626]" />
            </div>
          </div>
        )}

        {mode === "detailed" && (
          <div>
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Tarifa por provincia ($)</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto p-2 bg-[#0A0A0A] rounded border border-[#262626]" data-testid="ship-detailed-grid">
              {EC_PROVINCES_UI.map((p) => (
                <div key={p} className="flex items-center gap-1">
                  <span className="text-[11px] text-neutral-400 w-24 truncate" title={p}>{p}</span>
                  <Input
                    data-testid={`ship-rate-${p}`}
                    type="number" step="0.01" min="0"
                    value={rates[p] ?? ""}
                    onChange={(e) => setRates((r) => ({ ...r, [p]: e.target.value }))}
                    className="bg-[#171717] border-[#262626] h-7 text-xs"
                  />
                </div>
              ))}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">Todas las provincias son obligatorias en modo detallado.</div>
          </div>
        )}

        <label className={`flex items-start gap-3 ${sellerProvince ? "cursor-pointer" : "opacity-50"}`} data-testid="ship-local-only">
          <Checkbox
            checked={localOnly}
            disabled={!sellerProvince}
            onCheckedChange={(v) => setLocalOnly(!!v)}
            data-testid="ship-local-only-input"
            className="mt-0.5 border-[#404040] data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444]"
          />
          <div className="flex-1">
            <div className="text-sm text-white font-medium">Solo envíos dentro de {sellerProvince || "mi provincia"}</div>
            <div className="text-xs text-neutral-500">{sellerProvince ? "Viene marcado al cargar cartas, sellados, accesorios y lotes. Quien busque desde otra provincia no las verá. Tus publicaciones ya cargadas no cambian." : "Agrega tu provincia en tu perfil para usarlo."}</div>
          </div>
        </label>

        <div className="pt-3 border-t border-[#262626]">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={meetup}
              onCheckedChange={(v) => setMeetup(!!v)}
              data-testid="ship-meetup-enabled"
              className="mt-0.5 border-[#404040] data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
            />
            <div className="flex-1">
              <div className="text-sm text-white font-medium">Ofrecer encuentro presencial (envío $0)</div>
              <div className="text-xs text-neutral-500">Solo disponible para compradores en tu misma provincia. El pago sigue retenido hasta que el comprador confirme la entrega.</div>
            </div>
          </label>
          {meetup && (
            <div className="mt-3">
              <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Lugares sugeridos / Puntos de encuentro <span className="text-[#EF4444]">*</span></Label>
              <Textarea data-testid="ship-meetup-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: Metro Norte los sábados en la mañana, centros comerciales céntricos, etc." required className={`bg-[#171717] ${meetup && !notes.trim() ? "border-red-500" : "border-[#262626]"}`} />
              {meetup && !notes.trim() && (
                <div className="mt-1.5 text-xs text-red-400" data-testid="meetup-notes-error">
                  Obligatorio si activas encuentro presencial — sin lugares, el comprador no verá la opción en el carrito.
                </div>
              )}
            </div>
          )}
        </div>

        <Button data-testid="shipping-save" type="submit" disabled={saving} className="bg-[#EF4444] hover:bg-[#DC2626] w-full sm:w-auto">
          <BusyLabel busy={saving} text="Guardando…">{currentConfig ? "Actualizar configuración de envío" : "Guardar configuración de envío"}</BusyLabel>
        </Button>
        {currentConfig?.updated_at && (
          <div className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} />Última actualización: {new Date(currentConfig.updated_at).toLocaleString("es-EC")}</div>
        )}
      </form>
    </div>
  );
}

// ================ PAYOUT ================
export function PayoutForm({ currentPayout, user, onSaved }) {
  // Tiendas: el titular de la cuenta empresarial suele estar a nombre del RUC,
  // no de la cédula personal del dueño. Aceptamos ambos.
  const isStore = user?.account_type === "store";
  const userCedula = user?.cedula || "";
  const userRuc = user?.ruc || "";
  const [form, setForm] = useState({
    bank_name: currentPayout?.bank_name || "",
    account_type: currentPayout?.account_type || "ahorros",
    account_number: currentPayout?.account_number || "",
    holder_name: currentPayout?.holder_name || "",
    holder_cedula: currentPayout?.holder_cedula || (isStore ? userRuc : userCedula) || "",
  });
  const [saving, setSaving] = useState(false);
  const [interbankFee, setInterbankFee] = useState(0.40);

  useEffect(() => {
    api.get("/config/public").then((r) => {
      if (r.data?.interbank_transfer_fee != null) setInterbankFee(Number(r.data.interbank_transfer_fee));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (currentPayout) {
      setForm({
        bank_name: currentPayout.bank_name || "",
        account_type: currentPayout.account_type || "ahorros",
        account_number: currentPayout.account_number || "",
        holder_name: currentPayout.holder_name || "",
        holder_cedula: currentPayout.holder_cedula || "",
      });
    }
  }, [currentPayout]);

  const isPichincha = (form.bank_name || "").toLowerCase().includes("pichincha");
  const showInterbankWarn = form.bank_name.trim().length > 0 && !isPichincha;

  // Validación distinta según tipo de cuenta:
  //  · Persona natural → debe coincidir con la cédula verificada (10 dígitos).
  //  · Tienda → puede ser el RUC verificado (13 dígitos) O la cédula del
  //    representante. NUNCA se debe rechazar por no coincidir con la cédula.
  const holder = (form.holder_cedula || "").trim();
  const personMismatch = !isStore && userCedula && holder && holder !== userCedula.trim();
  const storeInvalid = isStore && holder && holder !== userRuc.trim() && holder !== userCedula.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (isStore) {
      if (!holder) { toast.error("Ingresa el RUC o cédula del titular de la cuenta"); return; }
      // El backend acepta cualquier valor no vacío para tiendas — el admin
      // valida contra el certificado bancario en la revisión de verificación.
    } else if (holder !== userCedula.trim()) {
      toast.error("La cédula del titular debe coincidir con tu cédula verificada");
      return;
    }
    setSaving(true);
    try {
      await api.put("/seller/payout-info", form);
      toast.success("Datos de cobro guardados");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-6" data-testid="payout-form-container">
      <div className="flex items-center gap-2 mb-2">
        <Landmark size={20} className="text-[#EF4444]" />
        <h2 className="font-display text-2xl">Datos de Cobro</h2>
      </div>
      <p className="text-sm text-neutral-400 mb-4">
        {isStore
          ? <>El administrador usará estos datos para transferirte tu neto tras cada venta cerrada. Como tu cuenta es de <b className="text-white">tienda</b>, la cuenta bancaria puede estar <b className="text-white">a nombre del RUC/negocio</b> o <b className="text-white">a tu nombre personal como dueño del RUC</b>. Ambas se aceptan — el admin valida contra el certificado bancario que subiste en verificación.</>
          : <>El administrador usará estos datos para transferirte tu neto tras cada venta cerrada. Por seguridad, <b className="text-white">la cédula del titular debe coincidir con tu cédula verificada</b>.</>}
      </p>
      <div className="text-xs text-neutral-500 mb-6 p-2 bg-[#0A0A0A] rounded border border-[#262626]" data-testid="payout-verified-ids">
        {isStore ? (
          <div className="space-y-0.5">
            <div>RUC verificado: <span className="font-mono text-white">{userRuc || "—"}</span></div>
            <div>Cédula representante: <span className="font-mono text-white">{userCedula || "—"}</span></div>
          </div>
        ) : (
          <div>Tu cédula verificada: <span className="font-mono text-white">{userCedula || "—"}</span></div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Banco</Label>
            <BankSelect
              testId="payout-bank"
              value={form.bank_name}
              onChange={(v) => setForm(f => ({ ...f, bank_name: v }))}
              required
            />
          </div>
          {showInterbankWarn && (
            <div
              data-testid="payout-interbank-warning"
              className="sm:col-span-2 bg-amber-500/10 border border-amber-500/40 rounded-md p-3 text-sm"
            >
              <b className="text-amber-200">Aviso importante — costo interbancario</b>
              <p className="text-neutral-300 mt-1 leading-snug">
                Las cuentas de otros bancos tienen un costo de transferencia interbancaria de <b className="text-white">${interbankFee.toFixed(2)}</b> que se descontará de <b className="text-white">cada pago que recibas</b>.
                Con una cuenta de <b className="text-white">Banco Pichincha</b>, tus pagos <b>no</b> tienen ese descuento.
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Tipo de cuenta</Label>
            <Select value={form.account_type} onValueChange={(v) => setForm(f => ({ ...f, account_type: v }))}>
              <SelectTrigger data-testid="payout-type" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white">
                <SelectItem value="ahorros">Ahorros</SelectItem>
                <SelectItem value="corriente">Corriente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">Número de cuenta</Label>
            <Input data-testid="payout-account" inputMode="numeric" value={form.account_number} onChange={(e) => setForm(f => ({ ...f, account_number: digitsOnly(e.target.value, 30) }))} placeholder="Solo números" required className="bg-[#171717] border-[#262626] font-mono" />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">
              {isStore ? "Nombre del titular (empresa o representante)" : "Nombre del titular"}
            </Label>
            <Input data-testid="payout-holder-name" value={form.holder_name} onChange={(e) => setForm(f => ({ ...f, holder_name: e.target.value }))} placeholder={isStore ? "Ej: HitCards Ecuador S.A." : "Como aparece en tu cuenta"} required className="bg-[#171717] border-[#262626]" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-400 mb-2 block uppercase tracking-wider">
              {isStore ? "RUC o cédula del titular" : "Cédula del titular"}
            </Label>
            <Input
              data-testid="payout-holder-cedula"
              inputMode="numeric"
              value={form.holder_cedula}
              onChange={(e) => setForm(f => ({ ...f, holder_cedula: digitsOnly(e.target.value, 13) }))}
              placeholder={isStore ? "RUC (13 dígitos) o cédula (10 dígitos)" : "Debe coincidir con tu cédula verificada"}
              required
              className={`bg-[#171717] border-[#262626] font-mono ${(personMismatch || storeInvalid) ? "border-red-500" : ""}`}
            />
            {personMismatch && (
              <div className="text-xs text-red-400 mt-1" data-testid="payout-cedula-mismatch">La cédula del titular debe coincidir con tu cédula verificada</div>
            )}
            {storeInvalid && (
              <div className="text-xs text-neutral-300 mt-1 p-2 bg-blue-500/10 border border-blue-500/30 rounded" data-testid="payout-store-warning">
                <b className="text-blue-200">Info:</b> el titular no coincide con tu RUC ni con tu cédula. Está bien — puedes guardar. El admin lo revisará contra tu certificado bancario cuando verifique la tienda.
              </div>
            )}
          </div>
        </div>
        <Button data-testid="payout-save" type="submit" disabled={saving || personMismatch} className="bg-[#EF4444] hover:bg-[#DC2626] w-full sm:w-auto">
          <BusyLabel busy={saving} text="Guardando…">{currentPayout ? "Actualizar datos de cobro" : "Guardar datos de cobro"}</BusyLabel>
        </Button>
        {currentPayout && (
          <div className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} />Última actualización: {currentPayout.updated_at ? new Date(currentPayout.updated_at).toLocaleString("es-EC") : "—"}</div>
        )}
      </form>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`bg-[#111] border rounded-md p-4 stat-bar ${highlight ? "border-[#EF4444]" : "border-[#262626]"}`}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wider"><Icon size={14} /> {label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}
